'use client';

import type { InitialConfigType } from '@lexical/react/LexicalComposer';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { createBeautifulMentionNode } from 'lexical-beautiful-mentions';
import { MentionComponent } from './mentions/MentionComponent';
import { Box, css } from '@terra/ui-styled-system';
import { forwardRef, useCallback, useEffect, useRef, type Ref } from 'react';
import { EditorTextArea } from './EditorTextArea';
import { EditorFooter } from './EditorFooter';
import type { ImperativeEditorRef } from './plugins/ImperativeRefPlugin';
import { ImperativeRefPlugin } from './plugins/ImperativeRefPlugin';
import { MessageHistoryPlugin } from './plugins/MessageHistoryPlugin';
import { useMessageHistory } from './hooks/useMessageHistory';
import type { MessageHistoryEntry } from './hooks/createMessageHistoryController';
import { useMentions } from '../../mentions/ChatMentionsProvider';
import type { MentionEntity } from '@terra/shared-dto-chat-types';
import { MentionOverflowProvider } from './mentions/MentionOverflowContext';
import { MentionOverflowPlugin } from './mentions/MentionOverflowPlugin';
import {
  findExactSlashCommand,
  parseSlashCommandInput,
  type SlashCommandItem,
} from '../../slash-commands';

function assignImperativeRef<T>({
  ref,
  value,
}: {
  ref: Ref<T> | undefined;
  value: T | null;
}): void {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(value);

    return;
  }

  ref.current = value;
}

interface EditorComponentProps {
  onMentionsTrigger?: (position?: { x: number; y: number }) => void;
  onSubmit: (message: { content: string; mentions?: MentionEntity[] }) => void;
  onStop: () => void;
  onCycleMode?: () => void;
  slashCommands?: readonly SlashCommandItem[];
  statusLineContent?: React.ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;
  isSubmitDisabled?: boolean;
  allowEmptySubmit?: boolean;
  submitButtonAriaLabel?: string;
  onFileUploadClick?: () => void;
  showFileUpload?: boolean;
  footerLeftContent?: React.ReactNode;
  footerRightContent?: React.ReactNode;
  composerAttachments?: React.ReactNode;
  placeholder?: string;
  messageHistorySessionId?: string;
  onMessageHistoryEntryRestored?: (entry: MessageHistoryEntry) => void;
  variant?: 'default' | 'copilot';
}

const containerStyles = css({
  gap: 0,
  width: '100%',
  minWidth: 0,
  backgroundColor: 'bg.subtle',
  borderRadius: 'xl',
  padding: 1,
  border: '1px solid',
  borderColor: 'border.subtle',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'border.default',
  },
  '&:focus-within': {
    borderColor: 'border.default',
    backgroundColor: 'bg.subtle',
  },
});

const copilotContainerStyles = css({
  gap: 0,
  width: '100%',
  minWidth: 0,
  minHeight: 0,
  flexShrink: 0,
  backgroundColor: 'bg.default',
  borderRadius: 'l2',
  padding: 2,
  border: '1px solid',
  borderColor: 'border.muted',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'stretch',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'border.default',
  },
  '&:focus-within': {
    borderColor: 'border.default',
  },
});

const theme: InitialConfigType['theme'] = {
  paragraph: 'editor-paragraph',
  text: {
    base: 'editor-text',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
    underlineStrikethrough: 'editor-text-underlineStrikethrough',
  },
  beautifulMentions: {
    '@': css({
      px: '1',
      mx: '1px',
      borderRadius: 'sm',
      cursor: 'pointer',
    }),
    '@Focused': css({
      outline: 'none',
      boxShadow: 'md',
    }),
  },
};

export const EditorComponent = forwardRef<
  ImperativeEditorRef,
  EditorComponentProps
>(
  (
    {
      onMentionsTrigger,
      onSubmit,
      onStop,
      onCycleMode,
      slashCommands,
      statusLineContent,
      isLoading,
      isDisabled,
      isSubmitDisabled,
      allowEmptySubmit = false,
      submitButtonAriaLabel,
      onFileUploadClick,
      showFileUpload,
      footerLeftContent,
      footerRightContent,
      composerAttachments,
      placeholder,
      messageHistorySessionId,
      onMessageHistoryEntryRestored,
      variant = 'default',
    },
    ref,
  ) => {
    const editorRef = useRef<ImperativeEditorRef>(null);
    const { selectedMentions, restoreSelectedMentionsForHistory } =
      useMentions();
    const selectedMentionsRef = useRef(selectedMentions);

    useEffect(() => {
      selectedMentionsRef.current = selectedMentions;
    }, [selectedMentions]);

    const messageHistory = useMessageHistory({
      sessionId: messageHistorySessionId,
    });

    const getMentionSnapshot = useCallback(
      () => ({
        mentions: selectedMentionsRef.current.map((mention) => ({
          id: mention.id,
          label: mention.label,
          type: mention.type,
          mentionEntity: mention.mentionEntity,
        })),
        editorMentions: editorRef.current?.getMentions() ?? [],
      }),
      [],
    );

    const handleHistoryEntryRestored = useCallback(
      (entry: MessageHistoryEntry) => {
        restoreSelectedMentionsForHistory(
          entry.mentions.map((mention) => ({
            id: mention.id,
            label: mention.label,
            type: mention.type,
            mentionEntity: mention.mentionEntity,
          })),
        );
        onMessageHistoryEntryRestored?.(entry);
      },
      [onMessageHistoryEntryRestored, restoreSelectedMentionsForHistory],
    );

    const mergedEditorRef = useCallback(
      (instance: ImperativeEditorRef | null) => {
        editorRef.current = instance;
        assignImperativeRef({ ref, value: instance });
      },
      [ref],
    );

    const initialConfig: InitialConfigType = {
      namespace: 'EditorComponent',
      theme,
      onError: (error: Error) => {
        console.error(error);
      },
      nodes: [...createBeautifulMentionNode(MentionComponent)],
    };

    const handleSubmit = () => {
      if (isDisabled) return;
      const rawContent = editorRef.current?.getContent() ?? '';

      if (slashCommands && slashCommands.length > 0) {
        const parsedSlash = parseSlashCommandInput(rawContent);

        if (parsedSlash) {
          const exactCmd = findExactSlashCommand({
            commands: slashCommands,
            commandName: parsedSlash.commandName,
          });

          if (exactCmd?.onExecute) {
            editorRef.current?.clearContent();
            messageHistory.resetNavigation();
            void exactCmd.onExecute({ rawArgs: parsedSlash.rawArgs });

            return;
          }
        }
      }

      const submitDisabled = isSubmitDisabled ?? isLoading;

      if (submitDisabled) return;

      if (
        rawContent.trim().length === 0 &&
        (!allowEmptySubmit || Boolean(isLoading))
      ) {
        return;
      }

      const editorStateJson =
        editorRef.current?.getEditorStateForHistory() ?? '';

      messageHistory.pushMessage({
        editorStateJson,
        plainText: rawContent,
        mentions: selectedMentionsRef.current.map((mention) => ({
          id: mention.id,
          label: mention.label,
          type: mention.type,
          mentionEntity: mention.mentionEntity,
        })),
        editorMentions: editorRef.current?.getMentions() ?? [],
      });
      onSubmit({ content: rawContent });
      editorRef.current?.clearContent();
      messageHistory.resetNavigation();
    };

    return (
      <LexicalComposer initialConfig={initialConfig}>
        <MentionOverflowProvider>
          <Box
            className={
              variant === 'copilot' ? copilotContainerStyles : containerStyles
            }
          >
            {composerAttachments ? (
              <Box w="full" minW={0} pb={1}>
                {composerAttachments}
              </Box>
            ) : null}
            <Box flexGrow={1} position="relative" minW={0} w="full">
              <EditorTextArea
                isLoading={isLoading}
                isDisabled={isDisabled}
                onMentionsTrigger={onMentionsTrigger}
                editorKey={0}
                onSubmit={handleSubmit}
                onStop={onStop}
                onCycleMode={onCycleMode}
                slashCommands={slashCommands}
                placeholder={placeholder}
                variant={variant}
              />
            </Box>
            <EditorFooter
              onSubmit={handleSubmit}
              onStop={onStop}
              isLoading={isLoading}
              isDisabled={isDisabled}
              isSubmitDisabled={isSubmitDisabled ?? isLoading}
              allowEmptySubmit={allowEmptySubmit}
              submitButtonAriaLabel={submitButtonAriaLabel}
              onFileUploadClick={onFileUploadClick}
              showFileUpload={showFileUpload}
              leftContent={footerLeftContent}
              rightContent={footerRightContent}
              variant={variant}
            />
          </Box>
          {statusLineContent}
          <MessageHistoryPlugin
            messageHistory={messageHistory}
            isDisabled={isDisabled}
            getMentionSnapshot={getMentionSnapshot}
            onEntryRestored={handleHistoryEntryRestored}
          />
          <ImperativeRefPlugin ref={mergedEditorRef} />
          <MentionOverflowPlugin />
        </MentionOverflowProvider>
      </LexicalComposer>
    );
  },
);
EditorComponent.displayName = 'EditorComponent';
